import React, { useEffect, useState, useCallback } from 'react';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import {
  Trophy, Sparkles, Loader2, Activity, ShieldCheck, AlertCircle,
  Video, FileText, CheckCircle2, RefreshCw, Award, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const DISCIPLINES = [
  { id: 'track', ar: 'مضمار (جري/سرعة)', en: 'Track (running)' },
  { id: 'field', ar: 'ميدان (وثب/رمي)', en: 'Field (jumps/throws)' },
  { id: 'swim',  ar: 'سباحة',              en: 'Swim' },
  { id: 'other', ar: 'أخرى',                en: 'Other' },
];
const DISABILITY = [
  { id: 'visual',     ar: 'بصرية',   en: 'Visual' },
  { id: 'cp',         ar: 'شلل دماغي', en: 'CP' },
  { id: 'limb',       ar: 'بتر',       en: 'Limb' },
  { id: 'wheelchair', ar: 'كرسي',     en: 'Wheelchair' },
  { id: 'physical',   ar: 'جسدية',     en: 'Physical' },
  { id: 'intellectual', ar: 'ذهنية',  en: 'Intellectual' },
];

function StatRow({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-stone-100 last:border-0">
      <span className="text-stone-600">{label}</span>
      <span className="font-mono font-bold">{value}{hint && <span className="text-[10px] text-stone-400 ms-1">{hint}</span>}</span>
    </div>
  );
}

export default function ClassificationCenter() {
  const { lang, user } = useApp();
  const isAr = lang === 'ar';
  const isReviewer = user && ['admin', 'teacher'].includes(user.role);

  const [tab, setTab] = useState('submit');
  const [sports, setSports] = useState([]);
  const [ipcClasses, setIpcClasses] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  // Submit form
  const [form, setForm] = useState({
    athlete_name: user?.full_name || '',
    age: '',
    gender: '',
    sport_id: '',
    discipline: 'track',
    disability_category: '',
    disability_details: '',
    affected_sides: '',
    mobility_aid: '',
    video_url: '',
    walks_unaided: true,
    can_run: true,
    avg_stride_cm: 100,
    asymmetry_pct: 20,
    range_of_motion_pct: 70,
    grip_strength_pct: 70,
    test_battery_id: '',
    test_results: {},
    notes_ar: '',
  });

  // Active IPC test battery (fetched when discipline+disability are selected)
  const [battery, setBattery] = useState(null);
  const [batteryLoading, setBatteryLoading] = useState(false);

  // Reviewer form
  const [review, setReview] = useState({ final_class_code: '', confirmed: true, classifier_notes_ar: '' });

  const load = useCallback(async () => {
    try {
      const [sp, ipc, mine] = await Promise.all([
        api.get('/training/sports?academy=paralympic'),
        api.get('/classification/ipc-classes'),
        api.get('/classification'),
      ]);
      setSports(sp.data);
      setIpcClasses(ipc.data);
      if (isReviewer) {
        setReviewQueue(mine.data.filter((s) => s.status === 'ai-analyzed' || s.status === 'submitted'));
        setMySubmissions(mine.data);
      } else {
        setMySubmissions(mine.data);
      }
    } catch (e) {
      // noop
    }
  }, [isReviewer]);
  useEffect(() => { load(); }, [load]);

  // Fetch the official IPC test battery whenever discipline + disability change
  useEffect(() => {
    if (!form.discipline || !form.disability_category) {
      setBattery(null);
      return;
    }
    let cancelled = false;
    setBatteryLoading(true);
    api.get(`/classification/protocols?discipline=${form.discipline}&category=${form.disability_category}`)
      .then(({ data }) => {
        if (cancelled) return;
        setBattery(data);
        setForm((f) => ({ ...f, test_battery_id: data.id, test_results: {} }));
      })
      .catch(() => { if (!cancelled) setBattery(null); })
      .finally(() => { if (!cancelled) setBatteryLoading(false); });
    return () => { cancelled = true; };
  }, [form.discipline, form.disability_category]);

  const setTestResult = (test_id, value) => {
    setForm((f) => ({ ...f, test_results: { ...f.test_results, [test_id]: value } }));
  };

  // Combined submit + analyze — issues a preliminary AI classification in a single click.
  // The user gets the IPC class immediately (no separate "run analysis" step). Sending
  // the file for official certified review remains an optional follow-up action.
  const submitAndAnalyze = async () => {
    if (!form.athlete_name || !form.age || !form.sport_id || !form.disability_category) {
      toast.error(isAr ? 'يرجى إكمال الحقول المطلوبة' : 'Complete required fields');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10), language: lang };
      const submitRes = await api.post('/classification/submit', payload);
      const cid = submitRes.data.id;
      setActive(submitRes.data);
      const { data } = await api.post(`/classification/${cid}/analyze`);
      setActive(data);
      setMySubmissions((m) => [data, ...m.filter((s) => s.id !== cid)]);
      toast.success(isAr ? 'صدر التصنيف الأولي ↓' : 'Preliminary classification ready ↓');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر إصدار التصنيف' : 'Classification failed'));
    }
    setBusy(false);
  };

  const analyze = async (cid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/classification/${cid}/analyze`);
      setActive(data);
      setMySubmissions((m) => m.map((s) => s.id === cid ? data : s));
      toast.success(isAr ? 'اكتمل التحليل الحركي' : 'Motion analysis complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed');
    }
    setBusy(false);
  };

  const submitReview = async () => {
    if (!active || !review.final_class_code) {
      toast.error(isAr ? 'اختر الفئة النهائية' : 'Select final class');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/classification/${active.id}/review`, review);
      setActive(data);
      toast.success(isAr ? 'تم اعتماد التصنيف' : 'Classification confirmed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Review failed');
    }
    setBusy(false);
  };

  return (
    <main id="main" className="bg-gradient-to-b from-white to-sky-50/30 min-h-screen py-8 px-4" data-testid="classification-center">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Badge className="mb-3 text-xs px-3 py-1 font-bold bg-sky-500/15 text-sky-700 border-sky-300">
            <Trophy className="w-3 h-3 me-1" />{isAr ? 'التصنيف الفني البارالمبي' : 'Paralympic Functional Classification'}
          </Badge>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black mb-2 leading-tight">{isAr ? 'تصنيف أولي فوري مدعوم بالذكاء الاصطناعي' : 'Instant AI-powered preliminary classification'}</h1>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? 'أدخل بيانات الرياضي ونتائج اختبارات IPC — يصدر النظام تصنيفاً أولياً فورياً. لاعتماد رسمي، أرسل لمراجعة مصنّف معتمد.'
              : 'Enter athlete data + IPC test results — the system issues a preliminary class instantly. Optional certified-classifier review afterwards.'}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap h-auto">
            <TabsTrigger value="submit" data-testid="cc-tab-submit"><Sparkles className="w-4 h-4 me-1" />{isAr ? 'تصنيف جديد' : 'New classification'}</TabsTrigger>
            <TabsTrigger value="mine" data-testid="cc-tab-mine"><FileText className="w-4 h-4 me-1" />{isAr ? 'طلباتي' : 'My submissions'}</TabsTrigger>
            {isReviewer && <TabsTrigger value="queue" data-testid="cc-tab-queue"><ShieldCheck className="w-4 h-4 me-1" />{isAr ? 'مراجعة المصنّف' : 'Reviewer queue'} <Badge className="ms-1" variant="outline">{reviewQueue.length}</Badge></TabsTrigger>}
          </TabsList>

          <TabsContent value="submit">
            <Card className="card-elevate">
              <CardContent className="p-6">
                <h2 className="text-lg font-black mb-3">{isAr ? '١) معلومات الرياضي والإعاقة' : '1) Athlete & disability'}</h2>
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div><Label className="text-xs">{isAr ? 'الاسم *' : 'Name *'}</Label><Input value={form.athlete_name} onChange={(e) => setForm({ ...form, athlete_name: e.target.value })} data-testid="cc-name" /></div>
                  <div><Label className="text-xs">{isAr ? 'العمر *' : 'Age *'}</Label><Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="cc-age" /></div>
                  <div>
                    <Label className="text-xs">{isAr ? 'الجنس' : 'Gender'}</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="cc-gender"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{isAr ? 'ذكر' : 'Male'}</SelectItem>
                        <SelectItem value="female">{isAr ? 'أنثى' : 'Female'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'الرياضة *' : 'Sport *'}</Label>
                    <Select value={form.sport_id} onValueChange={(v) => setForm({ ...form, sport_id: v })}>
                      <SelectTrigger data-testid="cc-sport"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>
                        {sports.map((s) => <SelectItem key={s.id} value={s.id}>{isAr ? s.name_ar : s.name_en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'التخصص' : 'Discipline'}</Label>
                    <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                      <SelectTrigger data-testid="cc-discipline"><SelectValue /></SelectTrigger>
                      <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d.id} value={d.id}>{isAr ? d.ar : d.en}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? 'فئة الإعاقة *' : 'Disability *'}</Label>
                    <Select value={form.disability_category} onValueChange={(v) => setForm({ ...form, disability_category: v })}>
                      <SelectTrigger data-testid="cc-disability"><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                      <SelectContent>{DISABILITY.map((d) => <SelectItem key={d.id} value={d.id}>{isAr ? d.ar : d.en}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">{isAr ? 'الجوانب المتأثرة' : 'Affected sides'}</Label><Input value={form.affected_sides} onChange={(e) => setForm({ ...form, affected_sides: e.target.value })} placeholder={isAr ? 'مثال: الطرف السفلي الأيمن' : 'e.g. right lower limb'} data-testid="cc-sides" /></div>
                  <div><Label className="text-xs">{isAr ? 'المعينات الحركية' : 'Mobility aid'}</Label><Input value={form.mobility_aid} onChange={(e) => setForm({ ...form, mobility_aid: e.target.value })} placeholder={isAr ? 'طرف صناعي / كرسي' : 'prosthesis / wheelchair'} data-testid="cc-aid" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs">{isAr ? 'رابط فيديو الأداء (اختياري)' : 'Performance video URL (optional)'}</Label><Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://..." data-testid="cc-video" /></div>
                </div>

                <h2 className="text-lg font-black mb-3 mt-2 flex items-center gap-2" data-testid="cc-battery-heading">
                  <ShieldCheck className="w-5 h-5 text-sky-700" />
                  {isAr ? '٢) بطارية اختبار IPC الرسميّة' : '2) Official IPC test battery'}
                </h2>
                {batteryLoading && (
                  <div className="bg-stone-50 border rounded-xl p-4 text-center text-sm text-muted-foreground" data-testid="cc-battery-loading">
                    <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
                    {isAr ? 'جاري تحميل بروتوكول الاختبار...' : 'Loading test protocol...'}
                  </div>
                )}
                {!batteryLoading && !battery && form.discipline && form.disability_category && (
                  <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-sm text-amber-900" data-testid="cc-battery-missing">
                    {isAr ? 'لا تتوفّر بطارية IPC رسميّة لهذا الاقتران بعد. سيتمّ الاعتماد على القياسات التقديريّة فقط.' : 'No official IPC battery is defined for this combination yet. Falling back to estimated metrics.'}
                  </div>
                )}
                {!batteryLoading && battery && (
                  <div className="space-y-3 mb-4" data-testid="cc-battery">
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs">
                      <strong className="text-sky-900">{isAr ? battery.title_ar : battery.title_en}</strong>
                      <span className="ms-2 text-sky-700">· {isAr ? 'الفئات المرشّحة:' : 'Candidate classes:'} {battery.candidate_classes.join(', ')}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {battery.tests.map((t) => {
                        const val = form.test_results[t.id];
                        if (t.type === 'numeric') {
                          return (
                            <div key={t.id} className="bg-stone-50 rounded-lg p-3" data-testid={`cc-test-${t.id}`}>
                              <Label className="text-xs font-bold flex items-center justify-between">
                                <span>{isAr ? t.name_ar : t.name_en}</span>
                                <Badge variant="outline" className="text-[10px] font-mono">{t.unit || ''}</Badge>
                              </Label>
                              {t.ref_ar && <div className="text-[10px] text-muted-foreground mt-0.5">{isAr ? t.ref_ar : (t.ref_en || t.ref_ar)}</div>}
                              <Input
                                type="number"
                                min={t.min}
                                max={t.max}
                                step={t.step || 0.1}
                                value={val ?? ''}
                                onChange={(e) => setTestResult(t.id, e.target.value === '' ? '' : parseFloat(e.target.value))}
                                className="mt-1"
                                data-testid={`cc-input-${t.id}`}
                              />
                            </div>
                          );
                        }
                        if (t.type === 'scale') {
                          return (
                            <div key={t.id} className="bg-stone-50 rounded-lg p-3" data-testid={`cc-test-${t.id}`}>
                              <div className="flex justify-between items-center mb-1">
                                <Label className="text-xs font-bold">{isAr ? t.name_ar : t.name_en}</Label>
                                <Badge variant="outline" className="text-[10px] font-mono font-bold">{val ?? t.min}{t.unit}</Badge>
                              </div>
                              {t.ref_ar && <div className="text-[10px] text-muted-foreground mb-1">{isAr ? t.ref_ar : (t.ref_en || t.ref_ar)}</div>}
                              <Slider
                                min={t.min} max={t.max} step={t.step || 1}
                                value={[val ?? t.min]}
                                onValueChange={(v) => setTestResult(t.id, v[0])}
                                data-testid={`cc-slider-${t.id}`}
                              />
                            </div>
                          );
                        }
                        if (t.type === 'binary') {
                          return (
                            <div key={t.id} className="bg-stone-50 rounded-lg p-3 flex items-center justify-between" data-testid={`cc-test-${t.id}`}>
                              <Label className="text-xs font-bold">{isAr ? t.name_ar : t.name_en}</Label>
                              <div className="flex gap-1">
                                <Button size="sm" variant={val === true ? 'default' : 'outline'} onClick={() => setTestResult(t.id, true)} data-testid={`cc-yes-${t.id}`}>{isAr ? 'نعم' : 'Yes'}</Button>
                                <Button size="sm" variant={val === false ? 'default' : 'outline'} onClick={() => setTestResult(t.id, false)} data-testid={`cc-no-${t.id}`}>{isAr ? 'لا' : 'No'}</Button>
                              </div>
                            </div>
                          );
                        }
                        if (t.type === 'enum') {
                          return (
                            <div key={t.id} className="bg-stone-50 rounded-lg p-3" data-testid={`cc-test-${t.id}`}>
                              <Label className="text-xs font-bold mb-1 block">{isAr ? t.name_ar : t.name_en}</Label>
                              <Select value={val || ''} onValueChange={(v) => setTestResult(t.id, v)}>
                                <SelectTrigger data-testid={`cc-enum-${t.id}`}><SelectValue placeholder={isAr ? 'اختر' : 'Select'} /></SelectTrigger>
                                <SelectContent>
                                  {t.options.map((o) => (
                                    <SelectItem key={o.v} value={o.v}>{isAr ? o.ar : o.en}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                    <div className="text-[10px] text-stone-500 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      {isAr ? 'هذه الاختبارات تعتمد على بروتوكول التصنيف الفنّي للجنة البارالمبية الدولية (IPC).' : 'Based on the official IPC functional classification protocol.'}
                    </div>
                  </div>
                )}

                <Textarea rows={2} placeholder={isAr ? 'ملاحظات إضافية...' : 'Additional notes...'} value={form.notes_ar} onChange={(e) => setForm({ ...form, notes_ar: e.target.value })} data-testid="cc-notes" />

                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button onClick={submitAndAnalyze} disabled={busy} className="bg-sky-700 hover:bg-sky-800 flex-1 sm:flex-initial min-h-11" data-testid="cc-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Sparkles className="w-4 h-4 me-2" />}
                    {isAr ? 'احصل على التصنيف الأولي' : 'Get preliminary classification'}
                  </Button>
                  {active?.id && active.ai_analysis && !active.classifier_review?.confirmed && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const { data } = await api.post(`/classification/${active.id}/analyze`);
                          setActive(data);
                          toast.success(isAr ? 'أعيد التحليل' : 'Re-analysed');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                        setBusy(false);
                      }}
                      disabled={busy}
                      className="min-h-11"
                      data-testid="cc-reanalyze"
                    >
                      <RefreshCw className="w-4 h-4 me-2" />{isAr ? 'أعد التحليل' : 'Re-analyse'}
                    </Button>
                  )}
                </div>

                {active?.ai_analysis && (
                  <div className="mt-6 border-t pt-5" data-testid="cc-analysis-result">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-5 h-5 text-amber-600" />
                      <h3 className="text-lg font-black">{isAr ? 'التصنيف الأولي التلقائي' : 'Instant preliminary classification'}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {isAr
                        ? 'صادر من محرك قواعد IPC + الذكاء الاصطناعي. لاعتماد رسمي، اطلب مراجعة مصنّف معتمد أدناه.'
                        : 'Issued by the IPC rules engine + AI. Request an officially certified review below for the final class.'}
                    </p>

                    <Card className="card-elevate mb-3">
                      <CardContent className="p-4">
                        <div className="grid sm:grid-cols-2 gap-x-6">
                          <StatRow label={isAr ? 'الإيقاع' : 'Cadence'} value={active.ai_analysis.biomechanics.cadence_steps_per_min} hint="steps/min" />
                          <StatRow label={isAr ? 'طول الخطوة' : 'Stride'} value={active.ai_analysis.biomechanics.avg_stride_cm} hint="cm" />
                          <StatRow label={isAr ? 'زمن التلامس' : 'Ground contact'} value={active.ai_analysis.biomechanics.ground_contact_ms} hint="ms" />
                          <StatRow label={isAr ? 'انثناء الركبة' : 'Knee flexion'} value={active.ai_analysis.biomechanics.peak_knee_flexion_deg} hint="°" />
                          <StatRow label={isAr ? 'هبوط الحوض' : 'Pelvic drop'} value={active.ai_analysis.biomechanics.pelvic_drop_deg} hint="°" />
                          <StatRow label={isAr ? 'مدى الحركة' : 'Range of motion'} value={active.ai_analysis.biomechanics.range_of_motion_pct + '%'} />
                          <StatRow label={isAr ? 'قوة القبضة' : 'Grip strength'} value={active.ai_analysis.biomechanics.grip_strength_pct + '%'} />
                          <StatRow label={isAr ? 'نقاط التماثل' : 'Symmetry score'} value={active.ai_analysis.biomechanics.symmetry_score + '/100'} />
                        </div>
                        <div className="text-[10px] text-stone-400 mt-2">{active.ai_analysis.engine}</div>
                      </CardContent>
                    </Card>

                    <Card className="card-elevate border-2 border-sky-200 bg-sky-50/60">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-sky-700" /><strong className="text-sky-900">{isAr ? 'الفئة المقترحة' : 'Suggested class'}</strong></div>
                        <div className="text-3xl font-black text-sky-800 mb-1">{active.ai_analysis.verdict.suggested_class}</div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge className="bg-sky-700 text-white text-[10px]">{isAr ? 'ثقة' : 'confidence'} {active.ai_analysis.verdict.confidence_pct}%</Badge>
                          {active.ai_analysis.verdict.rule_engine && (
                            <>
                              <Badge variant="outline" className="text-[10px]" data-testid="cc-completeness">
                                <ShieldCheck className="w-3 h-3 me-1" />
                                {isAr ? 'اكتمال البيانات' : 'Data completeness'} {active.ai_analysis.verdict.rule_engine.data_completeness_pct}%
                              </Badge>
                              {active.ai_analysis.verdict.rule_engine.battery_id && (
                                <Badge variant="outline" className="text-[10px]" data-testid="cc-battery-used">
                                  IPC: {active.ai_analysis.verdict.rule_engine.battery_id}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                        {active.ai_analysis.verdict.rule_engine?.rule_reason_ar && (
                          <div className="mb-3 text-xs bg-white/70 border border-sky-200 rounded p-2" data-testid="cc-rule-reason">
                            <strong className="text-sky-900">{isAr ? 'قاعدة IPC المُطابقة: ' : 'Matched IPC rule: '}</strong>
                            {isAr ? active.ai_analysis.verdict.rule_engine.rule_reason_ar : (active.ai_analysis.verdict.rule_engine.rule_reason_en || active.ai_analysis.verdict.rule_engine.rule_reason_ar)}
                          </div>
                        )}
                        <p className="text-sm leading-relaxed">{active.ai_analysis.verdict[isAr ? 'rationale_ar' : 'rationale_en'] || active.ai_analysis.verdict.rationale_ar}</p>
                        {Array.isArray(active.ai_analysis.verdict.alternatives) && active.ai_analysis.verdict.alternatives.length > 0 && (
                          <div className="mt-3 text-xs">
                            <strong>{isAr ? 'بدائل: ' : 'Alternatives: '}</strong>
                            {active.ai_analysis.verdict.alternatives.map((a) => (
                              <Badge key={a.code} variant="outline" className="me-1">{a.code}</Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {active.classifier_review?.confirmed && (
                      <div className="mt-3 bg-emerald-50 border border-emerald-300 rounded-xl p-3 flex items-center gap-2" data-testid="cc-certified">
                        <Award className="w-5 h-5 text-emerald-700" />
                        <div>
                          <div className="font-bold text-emerald-900">{isAr ? 'تصنيف معتمد' : 'Certified classification'}</div>
                          <div className="text-xs text-emerald-800">{isAr ? 'الفئة النهائية: ' : 'Final class: '}<strong>{active.classifier_review.final_class_code}</strong></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="mine">
            {mySubmissions.length === 0 ? (
              <div className="bg-stone-50 border rounded-xl p-8 text-center" data-testid="cc-mine-empty">
                <AlertCircle className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                <p className="font-bold">{isAr ? 'لا توجد طلبات بعد' : 'No submissions yet'}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cc-mine-grid">
                {mySubmissions.map((s) => (
                  <Card key={s.id} className="card-elevate cursor-pointer" onClick={() => { setActive(s); setTab('submit'); }} data-testid={`cc-mine-${s.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-bold text-sm">{s.athlete_name}</span>
                        <Badge variant="outline" className="text-[10px]">{s.status}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{s.sport_id} · {s.discipline} · {s.disability_category}</div>
                      {s.ai_analysis?.verdict && (
                        <div className="mt-2 text-xs"><strong>{isAr ? 'مقترح: ' : 'Suggested: '}</strong><Badge className="bg-sky-600 text-white">{s.ai_analysis.verdict.suggested_class}</Badge></div>
                      )}
                      {s.classifier_review?.confirmed && (
                        <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{isAr ? 'معتمد' : 'Certified'}: {s.classifier_review.final_class_code}</div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {isReviewer && (
            <TabsContent value="queue">
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 space-y-2" data-testid="cc-queue-list">
                  <h3 className="font-bold text-sm mb-2">{isAr ? 'قائمة المراجعة' : 'Pending reviews'} ({reviewQueue.length})</h3>
                  {reviewQueue.map((s) => (
                    <Card key={s.id} className={`cursor-pointer transition-all ${active?.id === s.id ? 'border-sky-500 border-2' : ''}`} onClick={() => { setActive(s); setReview({ final_class_code: s.ai_analysis?.verdict?.suggested_class || '', confirmed: true, classifier_notes_ar: '' }); }} data-testid={`cc-queue-item-${s.id}`}>
                      <CardContent className="p-3">
                        <div className="font-bold text-sm">{s.athlete_name}</div>
                        <div className="text-xs text-muted-foreground">{s.sport_id} · {s.discipline}</div>
                        {s.ai_analysis?.verdict && <Badge className="mt-1 bg-sky-600 text-white text-[10px]">AI: {s.ai_analysis.verdict.suggested_class}</Badge>}
                      </CardContent>
                    </Card>
                  ))}
                  {reviewQueue.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">{isAr ? 'لا توجد طلبات تنتظر المراجعة' : 'No items pending'}</div>}
                </div>

                <div className="lg:col-span-2">
                  {!active ? (
                    <div className="bg-stone-50 border rounded-xl p-8 text-center text-sm text-muted-foreground">{isAr ? 'اختر طلباً من القائمة لمراجعته' : 'Pick a submission to review'}</div>
                  ) : (
                    <Card className="card-elevate">
                      <CardContent className="p-5">
                        <h3 className="font-black text-lg mb-2">{active.athlete_name} · {active.age}</h3>
                        <div className="text-xs text-muted-foreground mb-3">{active.sport_id} · {active.discipline} · {active.disability_category} {active.affected_sides ? ` · ${active.affected_sides}` : ''}</div>
                        {!active.ai_analysis ? (
                          <Button onClick={() => analyze(active.id)} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="cc-queue-run">
                            <Sparkles className="w-4 h-4 me-2" />{isAr ? 'شغّل التحليل أولاً' : 'Run analysis first'}
                          </Button>
                        ) : (
                          <>
                            <Card className="bg-sky-50 border-sky-200 mb-3"><CardContent className="p-3">
                              <div className="text-xs text-muted-foreground mb-1">{isAr ? 'اقتراح الذكاء الاصطناعي' : 'AI suggestion'}</div>
                              <div className="text-2xl font-black text-sky-800">{active.ai_analysis.verdict.suggested_class}</div>
                              <div className="text-xs mt-1">{active.ai_analysis.verdict.rationale_ar}</div>
                            </CardContent></Card>

                            <Label className="text-xs">{isAr ? 'الفئة النهائية المعتمدة' : 'Final approved class'}</Label>
                            <Select value={review.final_class_code} onValueChange={(v) => setReview({ ...review, final_class_code: v })}>
                              <SelectTrigger className="mb-2" data-testid="cc-final-class"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent className="max-h-80">
                                {ipcClasses.filter((c) => c.discipline === active.discipline || active.discipline === 'other').map((c) => (
                                  <SelectItem key={c.code} value={c.code}>{c.code} — {isAr ? c.title_ar : c.title_en}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Textarea rows={2} placeholder={isAr ? 'ملاحظات المصنّف...' : 'Classifier notes...'} value={review.classifier_notes_ar} onChange={(e) => setReview({ ...review, classifier_notes_ar: e.target.value })} data-testid="cc-review-notes" />
                            <div className="mt-3 flex gap-2">
                              <Button onClick={() => { setReview({ ...review, confirmed: true }); submitReview(); }} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cc-confirm">
                                <CheckCircle2 className="w-4 h-4 me-2" />{isAr ? 'اعتمد + شهادة' : 'Confirm + certify'}
                              </Button>
                              <Button variant="outline" onClick={() => { setReview({ ...review, confirmed: false }); submitReview(); }} disabled={busy} data-testid="cc-reject">
                                <RefreshCw className="w-4 h-4 me-2" />{isAr ? 'أعد التقييم' : 'Re-assess'}
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </main>
  );
}
